import { ROTULO_TELA, TELAS, type Tela } from '@/features/auth/perfil-de-acesso';
import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, ShieldCheck, Trash2, Users, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  type AccessProfileRow,
  createAccessProfile,
  deleteAccessProfile,
  listAccessProfiles,
  updateAccessProfile,
} from '../api';

/** "Atendimento, Dashboard, Contatos" — Atendimento sempre primeiro (todo mundo tem). */
function rotuloDasTelas(telas: Tela[]): string {
  return ['Atendimento', ...TELAS.filter((t) => telas.includes(t)).map((t) => ROTULO_TELA[t])].join(
    ', ',
  );
}

const TH =
  'px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap';

/**
 * Perfis de Acesso — cada empresa monta perfis (nome + telas liberadas +
 * permissões no Atendimento) e escolhe o perfil de cada usuário em Usuários.
 * Admin tem tudo; atendente sem perfil fica só no Atendimento.
 */
export function PerfisAcessoTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['access-profiles'], queryFn: listAccessProfiles });
  const [editing, setEditing] = useState<AccessProfileRow | 'new' | null>(null);
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);

  const delMut = useMutation({
    mutationFn: deleteAccessProfile,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-profiles'] }),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.problem.title : 'Falha ao excluir', {
        ...(err instanceof ApiError && err.problem.detail
          ? { description: err.problem.detail }
          : {}),
      }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Perfis de Acesso
          </h2>
          <p className="text-[13px] text-n-500">
            Defina quais telas cada perfil acessa e escolha o perfil de cada usuário em Usuários.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Novo perfil
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'perfil' : 'perfis'}`
            : `Mostrando ${visible.length} de ${all.length} perfis`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className={TH}>Nome</th>
              <th className={TH}>Telas</th>
              <th className={cn(TH, 'w-[120px]')}>Usuários</th>
              <th className={cn(TH, 'w-[120px] text-right')}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-12 text-center text-n-500">
                  Nenhum perfil cadastrado. Sem perfil, o atendente acessa só o Atendimento.
                </td>
              </tr>
            )}
            {visible.map((p) => (
              <tr key={p.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] font-medium text-n-900">
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck size={15} strokeWidth={1.75} className="text-brand-500" />
                    {p.name}
                  </span>
                </td>
                <td className="px-[14px] py-[11px] text-n-700">
                  <span className="block max-w-[520px] truncate" title={rotuloDasTelas(p.screens)}>
                    {rotuloDasTelas(p.screens)}
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  <span className="inline-flex items-center gap-1.5 text-n-700">
                    <Users size={14} strokeWidth={1.75} className="text-n-400" />
                    {p.user_count}
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <ActionIconButton
                      icon={Pencil}
                      label="Editar"
                      tone="brand"
                      onClick={() => setEditing(p)}
                    />
                    <ActionIconButton
                      icon={Trash2}
                      label="Apagar"
                      tone="danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${p.name}"?`,
                          description:
                            p.user_count > 0
                              ? `${p.user_count} usuário(s) usam este perfil. Troque o perfil deles em Usuários antes de apagar.`
                              : 'Esta ação não pode ser desfeita.',
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(p.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={4} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <PerfilFormModal
          perfil={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function PerfilFormModal({
  perfil,
  onClose,
}: {
  perfil: AccessProfileRow | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState(perfil?.name ?? '');
  const [telas, setTelas] = useState<Set<Tela>>(new Set(perfil?.screens ?? []));
  const [nameError, setNameError] = useState<string | null>(null);

  const alternar = <T,>(set: Set<T>, valor: T): Set<T> => {
    const next = new Set(set);
    if (next.has(valor)) next.delete(valor);
    else next.add(valor);
    return next;
  };

  const mut = useMutation({
    mutationFn: async () => {
      const input = {
        name: name.trim(),
        screens: TELAS.filter((t) => telas.has(t)),
      };
      if (perfil) await updateAccessProfile(perfil.id, input);
      else await createAccessProfile(input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-profiles'] });
      void qc.invalidateQueries({ queryKey: ['settings-users'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setNameError('Já existe um perfil com esse nome.');
          return;
        }
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Erro ao salvar', { description: String(err) });
      }
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85vh] w-[520px] flex-col overflow-hidden rounded-lg bg-n-0 shadow-lg">
        <div className="flex flex-none items-center justify-between px-6 pt-6">
          <h3 className="font-bold text-[16px]">{perfil ? 'Editar perfil' : 'Novo perfil'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-2">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome do perfil</span>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              className={cn(
                'rounded-md border px-3 py-2 text-[13px] outline-none focus:border-brand-500',
                nameError ? 'border-danger-500' : 'border-n-200',
              )}
              placeholder="Ex.: Coordenação"
              maxLength={120}
              autoFocus
            />
            {nameError && <span className="text-[11px] text-danger-600">{nameError}</span>}
          </label>

          <p className="mt-5 mb-2 font-semibold text-[12px] text-n-700">Telas permitidas</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {/* Atendimento: todo perfil tem, com o mesmo de qualquer atendente
                (decisão do dono) — marcado e travado. "Visualizar tickets de
                outros usuários" e "Não receber via distribuição" são chaves do
                usuário, em Usuários. */}
            <label
              className="flex cursor-not-allowed items-center gap-2 rounded-md px-1 py-1.5 text-[13px] text-n-800"
              data-cp-tip="Todos os perfis têm o Atendimento"
            >
              <input
                type="checkbox"
                checked
                disabled
                className="h-4 w-4 flex-shrink-0 accent-brand-500"
                aria-label="Atendimento (todos os perfis têm)"
              />
              <span className="font-medium">Atendimento</span>
            </label>
            {TELAS.map((t) => (
              <label
                key={t}
                className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-[13px] text-n-800 hover:bg-n-50"
              >
                <input
                  type="checkbox"
                  checked={telas.has(t)}
                  onChange={() => setTelas((s) => alternar(s, t))}
                  className="h-4 w-4 flex-shrink-0 accent-brand-500"
                />
                {ROTULO_TELA[t]}
              </label>
            ))}
          </div>

        </div>

        <div className="flex flex-none justify-end gap-2 px-6 pt-3 pb-6">
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !name.trim()}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
